import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Box, Avatar, IconButton, Divider, Typography, Tooltip, Collapse } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import * as LucideIcons from 'lucide-react';
import { Settings, LogOut, Sun, Moon, Pin, PinOff, ChevronDown, ChevronRight, Search } from 'lucide-react';
import { GlobalSearch } from '../search';
import { useUIStore } from '../../stores/useUIStore';
import { useSignOut } from '@diggens/shared-hooks';
import { useAuthStore } from '@diggens/user-management';
import { UpdateNotificationButton } from '../UpdateNotificationButton';
import { MODULE_REGISTRY } from '@diggens/types/graph/modules';
import { CATEGORY_CONFIG, SYSTEM_CATEGORIES } from '../../config';
import { useSchema } from '../../contexts';
import { agentStyles } from '../../theme/theme';
import { ICON_SIZES, ICON_STROKE } from '../../constants/icons';

// Helper to get Lucide icon by name
function getIcon(iconName: string): React.ReactNode {
  const icons = LucideIcons as unknown as Record<string, React.ComponentType<{ size: number; strokeWidth: number }>>;
  const IconComponent = icons[iconName];
  if (!IconComponent) {
    return <LucideIcons.Circle size={ICON_SIZES.lg} strokeWidth={ICON_STROKE} />;
  }
  return <IconComponent size={ICON_SIZES.lg} strokeWidth={ICON_STROKE} />;
}

interface NavItem {
  path: string;
  label: string;
  icon: string;
  category: string;
  isDashboard?: boolean;
  isAnalytics?: boolean;
}

interface CategoryGroup {
  id: string;
  label: string;
  order: number;
  items: NavItem[];
}

export function Sidebar() {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const modules = useAuthStore((state) => state.modules);
  const signOut = useSignOut();
  const { nav: appNav } = useSchema();

  // Get enabled modules from AuthStore
  const enabledModules = useMemo(() => {
    if (!modules) return [];

    // Filter out 'pmn' and map to MODULE_REGISTRY
    const enabledIds = modules.filter((m) => m.module_id !== 'pmn' && m.access_level !== 'none').map((m) => m.module_id);

    return enabledIds.map((id) => MODULE_REGISTRY[id]).filter((m): m is NonNullable<typeof m> => m !== undefined);
  }, [modules]);

  const { sidebarPinned, sidebarOpen, setSidebarPinned, setSidebarOpen, collapsedCategories, toggleCategory, themeMode, toggleTheme } = useUIStore();

  // Agent status - TODO: Connect to actual agent state
  const [isAgentActive] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  // Global keyboard shortcut for search (⌘K / Ctrl+K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const isExpanded = sidebarPinned || sidebarOpen;

  // Build categorized nav items from enabled modules
  const categoryGroups = useMemo(() => {
    const groups: Record<string, CategoryGroup> = {};

    enabledModules.forEach((module) => {
      const categoryId = module.category;
      const categoryConfig = CATEGORY_CONFIG[categoryId];
      if (!categoryConfig) return;

      if (!groups[categoryId]) {
        groups[categoryId] = {
          id: categoryId,
          label: categoryConfig.label,
          order: categoryConfig.order,
          items: [],
        };
      }

      // Add dashboard link if module has one
      if (module.hasDashboard && module.dashboardPath) {
        groups[categoryId]?.items.push({
          path: module.dashboardPath,
          label: 'Dashboard',
          icon: 'LayoutDashboard',
          category: categoryId,
          isDashboard: true,
        });
      }

      // Add regular routes
      module.routes.forEach((route) => {
        groups[categoryId]?.items.push({
          path: route.path,
          label: route.label,
          icon: route.icon,
          category: categoryId,
        });
      });

      // Add analytics link if module has one
      if (module.hasAnalytics) {
        groups[categoryId]?.items.push({
          path: `/analytics?module=${module.id}`,
          label: 'Analytics',
          icon: 'BarChart3',
          category: categoryId,
          isAnalytics: true,
        });
      }
    });

    // Installed apps: one category per app, from the tenant schema
    appNav.forEach((category) => {
      const group: CategoryGroup = groups[category.id] ?? { id: category.id, label: category.label, order: category.order, items: [] };
      category.items.forEach((item) => {
        group.items.push({ path: item.path, label: item.label, icon: item.icon, category: category.id });
      });
      groups[category.id] = group;
    });

    return Object.values(groups).sort((a, b) => a.order - b.order);
  }, [enabledModules, appNav]);

  // System categories (Analytics)
  const systemCategories = useMemo(() => {
    return Object.values(SYSTEM_CATEGORIES).sort((a, b) => a.order - b.order);
  }, []);

  const handleSignOut = async () => {
    await signOut.mutateAsync();
    navigate('/login');
  };

  const getUserInitials = () => {
    if (user?.full_name) {
      return user.full_name.charAt(0).toUpperCase();
    }
    if (user?.email) {
      return user.email.charAt(0).toUpperCase();
    }
    return '?';
  };

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: isExpanded ? 240 : 72,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: isExpanded ? 240 : 72,
          boxSizing: 'border-box',
          transition: 'width 200ms',
          overflowX: 'hidden',
        },
      }}
      onMouseEnter={() => !sidebarPinned && setSidebarOpen(true)}
      onMouseLeave={() => !sidebarPinned && setSidebarOpen(false)}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header with pin toggle */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: isExpanded ? 'space-between' : 'center',
            minHeight: 64,
          }}
        >
          {isExpanded ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <img src="/logo.svg" alt="Diggens" style={{ height: '24px', width: 'auto' }} />
              <Typography variant="h6" noWrap>
                Diggens CRM
              </Typography>
            </Box>
          ) : (
            <img src="/logo.svg" alt="Diggens" style={{ height: '24px', width: 'auto' }} />
          )}
          <Tooltip title={sidebarPinned ? 'Unpin sidebar' : 'Pin sidebar'} placement="right">
            <IconButton size="small" onClick={() => setSidebarPinned(!sidebarPinned)} sx={{ opacity: isExpanded ? 1 : 0.5 }}>
              {sidebarPinned ? <PinOff size={ICON_SIZES.lg} strokeWidth={ICON_STROKE} /> : <Pin size={ICON_SIZES.lg} strokeWidth={ICON_STROKE} />}
            </IconButton>
          </Tooltip>
        </Box>

        <Divider />

        {/* Agent Status Indicator */}
        {isAgentActive && (
          <Box sx={{ p: 2 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: 1.5,
                borderRadius: 1,
                bgcolor: 'action.hover',
              }}
            >
              <Avatar sx={agentStyles.agentAvatar(theme.palette.mode)}>DA</Avatar>
              {isExpanded && (
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2">Data Analyst</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: agentStyles.statusColors.processing,
                        ...agentStyles.agentPulse,
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      Processing
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* Navigation List - Scrollable */}
        <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
          {/* Dashboard - always first */}
          <List sx={{ pt: 1 }}>
            <ListItem disablePadding sx={{ px: 1 }}>
              <Tooltip title={!isExpanded ? 'Dashboard' : ''} placement="right">
                <ListItemButton
                  onClick={() => navigate('/home')}
                  sx={{
                    borderRadius: 1,
                    minHeight: 48,
                    justifyContent: isExpanded ? 'initial' : 'center',
                    px: 2.5,
                    bgcolor: location.pathname === '/home' ? 'primary.main' : 'transparent',
                    color: location.pathname === '/home' ? 'primary.contrastText' : 'text.primary',
                    '&:hover': {
                      bgcolor: location.pathname === '/home' ? 'primary.dark' : 'action.hover',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: isExpanded ? 2 : 'auto',
                      justifyContent: 'center',
                      color: location.pathname === '/dashboard' ? 'primary.contrastText' : 'inherit',
                    }}
                  >
                    {getIcon('LayoutDashboard')}
                  </ListItemIcon>
                  {isExpanded && <ListItemText primary="Dashboard" />}
                </ListItemButton>
              </Tooltip>
            </ListItem>
          </List>

          {/* Category Groups */}
          {categoryGroups.map((category) => (
            <Box key={category.id}>
              {/* Category Header */}
              <ListItemButton
                onClick={() => toggleCategory(category.id)}
                sx={{
                  minHeight: 40,
                  px: 2,
                  py: 0.5,
                  justifyContent: isExpanded ? 'initial' : 'center',
                }}
              >
                {isExpanded ? (
                  <>
                    <Typography
                      variant="caption"
                      sx={{
                        flex: 1,
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        color: 'text.secondary',
                      }}
                    >
                      {category.label}
                    </Typography>
                    {collapsedCategories[category.id] ? <ChevronRight size={16} strokeWidth={ICON_STROKE} /> : <ChevronDown size={16} strokeWidth={ICON_STROKE} />}
                  </>
                ) : (
                  <Tooltip title={category.label} placement="right">
                    <ChevronRight size={16} strokeWidth={ICON_STROKE} />
                  </Tooltip>
                )}
              </ListItemButton>

              {/* Category Items */}
              <Collapse in={!collapsedCategories[category.id]} timeout="auto">
                <List disablePadding>
                  {category.items.map((item) => {
                    const isActive = location.pathname === item.path;
                    return (
                      <ListItem key={item.path} disablePadding sx={{ px: 1 }}>
                        <Tooltip title={!isExpanded ? item.label : ''} placement="right">
                          <ListItemButton
                            onClick={() => navigate(item.path)}
                            sx={{
                              borderRadius: 1,
                              minHeight: 40,
                              justifyContent: isExpanded ? 'initial' : 'center',
                              px: 2.5,
                              pl: isExpanded ? 4 : 2.5,
                              bgcolor: isActive ? 'primary.main' : 'transparent',
                              color: isActive ? 'primary.contrastText' : 'text.primary',
                              '&:hover': {
                                bgcolor: isActive ? 'primary.dark' : 'action.hover',
                              },
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                minWidth: 0,
                                mr: isExpanded ? 2 : 'auto',
                                justifyContent: 'center',
                                color: isActive ? 'primary.contrastText' : 'inherit',
                              }}
                            >
                              {getIcon(item.icon)}
                            </ListItemIcon>
                            {isExpanded && <ListItemText primary={item.label} />}
                          </ListItemButton>
                        </Tooltip>
                      </ListItem>
                    );
                  })}
                </List>
              </Collapse>
            </Box>
          ))}

          {/* System Categories (Analytics) */}
          {systemCategories.map((category) => (
            <Box key={category.id}>
              <Divider sx={{ my: 1 }} />
              {/* Category Header */}
              <ListItemButton
                onClick={() => toggleCategory(category.id)}
                sx={{
                  minHeight: 40,
                  px: 2,
                  py: 0.5,
                  justifyContent: isExpanded ? 'initial' : 'center',
                }}
              >
                {isExpanded ? (
                  <>
                    <Typography
                      variant="caption"
                      sx={{
                        flex: 1,
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        color: 'text.secondary',
                      }}
                    >
                      {category.label}
                    </Typography>
                    {collapsedCategories[category.id] ? <ChevronRight size={16} strokeWidth={ICON_STROKE} /> : <ChevronDown size={16} strokeWidth={ICON_STROKE} />}
                  </>
                ) : (
                  <Tooltip title={category.label} placement="right">
                    <ChevronRight size={16} strokeWidth={ICON_STROKE} />
                  </Tooltip>
                )}
              </ListItemButton>

              {/* Category Items */}
              <Collapse in={!collapsedCategories[category.id]} timeout="auto">
                <List disablePadding>
                  {category.routes.map((route) => {
                    const isActive = location.pathname === route.path;
                    // TODO: Check permissions for route.requiresPermission
                    return (
                      <ListItem key={route.path} disablePadding sx={{ px: 1 }}>
                        <Tooltip title={!isExpanded ? route.label : ''} placement="right">
                          <ListItemButton
                            onClick={() => navigate(route.path)}
                            sx={{
                              borderRadius: 1,
                              minHeight: 40,
                              justifyContent: isExpanded ? 'initial' : 'center',
                              px: 2.5,
                              pl: isExpanded ? 4 : 2.5,
                              bgcolor: isActive ? 'primary.main' : 'transparent',
                              color: isActive ? 'primary.contrastText' : 'text.primary',
                              '&:hover': {
                                bgcolor: isActive ? 'primary.dark' : 'action.hover',
                              },
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                minWidth: 0,
                                mr: isExpanded ? 2 : 'auto',
                                justifyContent: 'center',
                                color: isActive ? 'primary.contrastText' : 'inherit',
                              }}
                            >
                              {getIcon(route.icon)}
                            </ListItemIcon>
                            {isExpanded && <ListItemText primary={route.label} />}
                          </ListItemButton>
                        </Tooltip>
                      </ListItem>
                    );
                  })}
                </List>
              </Collapse>
            </Box>
          ))}
        </Box>

        <Divider />

        {/* Update Notification */}
        <UpdateNotificationButton />

        {/* User Context Section */}
        <Box sx={{ p: 2 }}>
          {/* User Info */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              mb: 1.5,
              justifyContent: isExpanded ? 'flex-start' : 'center',
            }}
          >
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: 'primary.main',
                fontSize: '0.875rem',
              }}
            >
              {getUserInitials()}
            </Avatar>
            {isExpanded && (
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" noWrap>
                  {user?.full_name || user?.email}
                </Typography>
                {user?.role !== 'super_admin' && user?.tenant_id && (
                  <Typography variant="caption" color="text.secondary" noWrap>
                    Tenant ID: {user.tenant_id.slice(0, 8)}...
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          {/* Action Buttons */}
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              justifyContent: isExpanded ? 'flex-start' : 'center',
              flexDirection: isExpanded ? 'row' : 'column',
            }}
          >
            {/* Global Search */}
            <Tooltip title="Search (⌘K)" placement="right">
              <IconButton onClick={() => setSearchOpen(true)} size="small">
                <Search size={ICON_SIZES.lg} strokeWidth={ICON_STROKE} />
              </IconButton>
            </Tooltip>

            {/* Settings */}
            <Tooltip title="Settings" placement="right">
              <IconButton onClick={() => navigate('/settings')} size="small">
                <Settings size={ICON_SIZES.lg} strokeWidth={ICON_STROKE} />
              </IconButton>
            </Tooltip>

            {/* Theme Toggle */}
            <Tooltip title={`Switch to ${themeMode === 'light' ? 'dark' : 'light'} mode`} placement="right">
              <IconButton onClick={toggleTheme} size="small">
                {themeMode === 'light' ? <Moon size={ICON_SIZES.lg} strokeWidth={ICON_STROKE} /> : <Sun size={ICON_SIZES.lg} strokeWidth={ICON_STROKE} />}
              </IconButton>
            </Tooltip>

            {/* Logout */}
            <Tooltip title="Sign out" placement="right">
              <IconButton onClick={handleSignOut} size="small" disabled={signOut.isPending}>
                <LogOut size={ICON_SIZES.lg} strokeWidth={ICON_STROKE} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
    </Drawer>
  );
}
